import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  BarChart3, 
  Users, 
  CheckCircle, 
  AlertTriangle,
  Clock,
  TrendingUp,
  Loader2
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Progress } from '../../../components/ui/progress';
import { API_BASE } from '../../../config/api';

const API = `${API_BASE}/api`;

export default function ProgressStep({ programmeId, programme, token, headers }) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (programmeId) {
      fetchDashboard();
    } else {
      setLoading(false);
    }
  }, [programmeId]);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/programmes/${programmeId}/progress-dashboard`, { headers });
      setDashboard(res.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!programmeId) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">Save Programme First</h3>
        <p className="text-zinc-400">Complete Step 1 to monitor progress</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">No data available</h3>
        <p className="text-zinc-400">Enroll learners to see progress</p>
      </div>
    );
  }

  const { progress_stats, learner_progress, total_learners, average_progress, total_activities } = dashboard;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="bg-zinc-800/50 border-zinc-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <Users className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{total_learners}</p>
                <p className="text-xs text-zinc-400">Total Learners</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-800/50 border-zinc-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-zinc-700">
                <Clock className="h-5 w-5 text-zinc-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{progress_stats.not_started}</p>
                <p className="text-xs text-zinc-400">Not Started</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-800/50 border-zinc-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <TrendingUp className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{progress_stats.in_progress}</p>
                <p className="text-xs text-zinc-400">In Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-800/50 border-zinc-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <CheckCircle className="h-5 w-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{progress_stats.completed}</p>
                <p className="text-xs text-zinc-400">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-800/50 border-zinc-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/20">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{progress_stats.at_risk}</p>
                <p className="text-xs text-zinc-400">At Risk</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Average Progress */}
      <Card className="bg-zinc-800/50 border-zinc-700">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Overall Programme Progress</h3>
            <span className="text-2xl font-bold text-teal-400">{average_progress}%</span>
          </div>
          <Progress value={average_progress} className="h-3" />
          <p className="text-sm text-zinc-400 mt-2">
            Average completion across {total_learners} learners and {total_activities} activities
          </p>
        </CardContent>
      </Card>

      {/* Learner Progress List */}
      <div className="space-y-2">
        <h3 className="text-lg font-semibold text-white mb-4">Learner Progress</h3>
        
        {learner_progress.length === 0 ? (
          <p className="text-zinc-400 text-center py-8">No learners enrolled yet</p>
        ) : (
          <div className="space-y-3">
            {learner_progress.map((learner, idx) => (
              <div 
                key={idx}
                className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg border border-zinc-700"
              >
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center text-white font-medium">
                    {learner.name?.charAt(0) || learner.email?.charAt(0) || '?'}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-white">
                        {learner.name || learner.email}
                      </p>
                      {learner.is_at_risk && (
                        <Badge className="bg-red-500/20 text-red-400">At Risk</Badge>
                      )}
                    </div>
                    <p className="text-sm text-zinc-400">
                      {learner.completed_activities} / {learner.total_activities} activities
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 w-64">
                  <Progress value={learner.progress} className="flex-1 h-2" />
                  <span className="text-sm font-medium text-zinc-300 w-12 text-right">
                    {learner.progress}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
